import { Info, Users, UserPlus } from 'lucide-react';
import { useRecordContext, useTranslate } from 'ra-core';
import { DataTable } from '../../components/admin/data-table';
import { ReferenceArrayField } from '../../components/admin/reference-array-field';
import { SingleFieldList } from '../../components/admin/single-field-list';
import { TextField } from '../../components/admin/text-field';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { ActionLink } from '../../components/custom/ActionLink';
import { RowActions } from '../../components/custom/RowActions';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { useCanAccessUsersResource } from '../../auth/useCanAccessUsersResource';
import { MoonstarList } from '../Moonstar/MoonstarList';
import { RoleBulkActions } from './RoleBulkActions';

export const RoleList = () => (
  <MoonstarList sort={{ field: 'name', order: 'ASC' }}>
    <DataTable
      bulkActionButtons={<RoleBulkActions />}
      isRowSelectable={(record) => !record.systemType}
    >
      <DataTable.Col source="name" />
      <DataTable.Col source="systemType" />
      <DataTable.Col source="permissionIds" disableSort>
        <ReferenceArrayField source="permissionIds" reference="permissions">
          <SingleFieldList className="flex-wrap">
            <Badge variant="outline">
              <TextField source="name" />
            </Badge>
          </SingleFieldList>
        </ReferenceArrayField>
      </DataTable.Col>
      <DataTable.Col className="w-px whitespace-nowrap">
        <RoleActions />
      </DataTable.Col>
    </DataTable>
  </MoonstarList>
);

const RoleActions = () => {
  const translate = useTranslate();
  const record = useRecordContext();
  const isUserRole = record?.systemType === 'user';
  const canAccessUsers = useCanAccessUsersResource();

  if (!record || !canAccessUsers) {
    return null;
  }

  // Doesn't make sense to be able to manage users in the "users" roles, as all users are already in that role.
  return (
    <RowActions>
      <ActionLink
        to={`/roles/${record.id}/users`}
        icon={<Users />}
        disabled={isUserRole}
      >
        {translate('moonstar.roleUsers.title')}
      </ActionLink>
      <ActionLink
        to={`/roles/${record.id}/users/add`}
        icon={<UserPlus />}
        disabled={isUserRole}
      >
        {translate('moonstar.roleUsers.add.label')}
      </ActionLink>
      {isUserRole && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={translate('moonstar.roleUsers.userRoleTooltip')}
                />
              }
            >
              <Info />
            </TooltipTrigger>
            <TooltipContent>
              {translate('moonstar.roleUsers.userRoleTooltip')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </RowActions>
  );
};
